import { fanduel as fd } from "@fanduel/formation-tokens";
import cx from "classnames";
import { scaleLinear, extent, curveStep, line, area, scaleTime } from "d3";
import ChartAxis from "./ChartAxis";
import { TeamDisplay } from "./LineMovement";
import { MarketName, Team } from "./SingleMarketCard";
import { TeamGradient } from "./SvgUtils";
import { filterEmptyLineData, makeAmericanOddsLinear } from "./utils";
import styles from "./lineChart.module.scss";

const getDummyRangeForEmptyData = (toggle: boolean, market: MarketName) => {
  if (toggle && market === MarketName.Total) {
    return [35, 55];
  } else if (toggle && market === MarketName.Spread) {
    return [1.5, 5];
  }
  return [50, -250];
};

export type LinePlotData = (number | undefined)[];

export const marginTop = 10;
export const marginRight = 22;
export const marginBottom = 12;
export const marginLeft = 0;
export const stroke = 1.5;
interface LinePlotProps {
  home: Team;
  away: Team;
  width?: number;
  height?: number;
  oddsSuggestedSegments: number;
  time: LinePlotData;
  show: TeamDisplay;
  market: MarketName;
  gameId: number;
  toggle: boolean;
}

export default function LinePlot({
  home,
  away,
  width = 200,
  height = 100,
  oddsSuggestedSegments = 5,
  time,
  show,
  market,
  gameId,
  toggle,
}: LinePlotProps) {
  // !toggle = show the odds toggle = show the line value
  const showHomeOdds =
    (show === TeamDisplay.Home || show === TeamDisplay.Both) && !toggle;
  const showAwayOdds =
    (show === TeamDisplay.Away || show === TeamDisplay.Both) && !toggle;
  const showUnifiedLine = show === TeamDisplay.Both && toggle;
  const showHomeLine = show === TeamDisplay.Home && toggle;
  const showAwayLine = show === TeamDisplay.Away && toggle;

  const svgId = `svg-${market}-${gameId}`;

  const formattedHomeOdds = makeAmericanOddsLinear(
    filterEmptyLineData(home.odds)
  );
  const formattedAwayOdds = makeAmericanOddsLinear(
    filterEmptyLineData(away.odds)
  );
  const formattedHomeLine = filterEmptyLineData(home.line ?? []);
  const formattedAwayLine = filterEmptyLineData(away.line ?? []);
  const formattedUnifiedLine = filterEmptyLineData(home.line ?? []).map(
    (point) => Math.abs(point)
  );

  const isEmpty =
    !(formattedHomeOdds.length > 0) && !(formattedAwayOdds.length > 0);

  let fullRange: number[] = [];

  if (isEmpty) {
    fullRange = getDummyRangeForEmptyData(toggle, market);
  } else if (showHomeOdds && showAwayOdds) {
    fullRange = [...formattedHomeOdds, ...formattedAwayOdds];
  } else if (showHomeOdds) {
    fullRange = formattedHomeOdds;
  } else if (showAwayOdds) {
    fullRange = formattedAwayOdds;
  } else if (showUnifiedLine) {
    fullRange = formattedHomeLine.map((point) => Math.abs(point));
  } else if (showHomeLine) {
    fullRange = formattedHomeLine;
  } else if (showAwayLine) {
    fullRange = formattedAwayLine;
  }

  const startDate = new Date(time[0] ?? new Date());
  const endDate = new Date(time.at(-1) ?? new Date());
  const timeScale = scaleTime()
    .domain([startDate, endDate])
    .range([marginLeft, width - marginRight]);

  const x = scaleLinear(
    [0, time.length - 1],
    [marginLeft, width - marginRight]
  );

  const lineRange = extent(fullRange) as [number, number];

  const changePercent =
    Math.abs(lineRange[1] - lineRange[0]) / Math.abs(lineRange[1]);

  // A spread or total line with minimal movement
  if (
    lineRange[0] === lineRange[1] ||
    (Math.abs(lineRange[0]) < 100 && Math.abs(lineRange[1] - lineRange[0]) < 2)
  ) {
    lineRange[0] = lineRange[0] + 1.5;
    lineRange[1] = lineRange[1] - 1.5;
  } else if (changePercent < 0.05) {
    // Add some padding to lines with minimal movement
    lineRange[0] = lineRange[0] * (1 - changePercent);
    lineRange[1] = lineRange[1] * (1 + changePercent);
  }

  const y = scaleLinear(lineRange, [marginTop, height - marginBottom]).nice(
    oddsSuggestedSegments
  );
  const stepLine = line<number>()
    .x((d, i) => x(i))
    .y((d) => y(d))
    .curve(curveStep);

  const areaSvg = area<number>()
    .x((d, i) => x(i))
    .y0(height - marginBottom)
    .y1((d) => y(d))
    .curve(curveStep);

  const homeId = `home-gradient-${market}-${home.color}`;
  const awayId = `away-gradient-${market}-${away.color}`;
  const unifiedId = `unifiedGradient=${market}-${fd.colors.brandPrimaryDefault}`;
  return (
    <div
      className={cx(styles.chartContainer, isEmpty && styles.oddsUnavailable)}
    >
      <ChartAxis
        svgId={svgId}
        market={market}
        gameId={gameId}
        show={show}
        timeScale={timeScale}
        startDate={startDate}
        endDate={endDate}
        y={y}
        oddsSuggestedSegments={oddsSuggestedSegments}
        width={width}
        toggle={toggle}
        showUnifiedLine={showUnifiedLine}
      />
      <div className={styles.mainArea}>
        <svg
          className={styles.svgContainer}
          viewBox={`0 0 ${width} ${height}`}
          id={svgId}
        >
          <defs>
            <TeamGradient teamId={homeId} teamColor={home.color} />
            <TeamGradient teamId={awayId} teamColor={away.color} />
            <TeamGradient
              teamId={unifiedId}
              teamColor={fd.colors.brandPrimaryDefault}
            />
          </defs>
          {showAwayOdds && (
            <path
              fill={`url(#${awayId})`}
              stroke="none"
              d={areaSvg(formattedAwayOdds) || ""}
            />
          )}
          {showHomeOdds && (
            <path
              fill={`url(#${homeId})`}
              stroke="none"
              d={areaSvg(formattedHomeOdds) || ""}
            />
          )}
          {showAwayLine && (
            <path
              fill={`url(#${awayId})`}
              stroke="none"
              d={areaSvg(formattedAwayLine) || ""}
            />
          )}
          {showHomeLine && (
            <path
              fill={`url(#${homeId})`}
              stroke="none"
              d={areaSvg(formattedHomeLine) || ""}
            />
          )}
          {showUnifiedLine && (
            <path
              fill={`url(#${unifiedId})`}
              stroke="none"
              d={areaSvg(formattedUnifiedLine) || ""}
            />
          )}
          {showAwayOdds && (
            <path
              fill="none"
              stroke={away.color}
              strokeWidth={stroke}
              d={stepLine(formattedAwayOdds) || ""}
            />
          )}
          {showHomeOdds && (
            <path
              fill="none"
              stroke={home.color}
              strokeWidth={stroke}
              d={stepLine(formattedHomeOdds) || ""}
            />
          )}
          {showAwayLine && (
            <path
              fill="none"
              stroke={away.color}
              strokeWidth={stroke}
              d={stepLine(formattedAwayLine) || ""}
            />
          )}
          {showHomeLine && (
            <path
              fill="none"
              stroke={home.color}
              strokeWidth={stroke}
              d={stepLine(formattedHomeLine) || ""}
            />
          )}
          {showUnifiedLine && (
            <path
              fill="none"
              stroke={fd.colors.brandPrimaryDefault}
              strokeWidth={stroke}
              d={stepLine(formattedUnifiedLine) || ""}
            />
          )}
        </svg>
      </div>
    </div>
  );
}
